/* eslint no-alert: warn */
import type { CollectionError } from '../../error/CollectionError.js';
import type { ClientErrorCode } from '../../error/index.js';
import type { PaymentResponseApi } from '../../payment/context/createPaymentResponseApi.js';
import { createPayment, type PostMiddlewareFn, type PreMiddlewareFn } from '../../payment/createPayment.js';
import { type PaymentConfig } from '../../service/PaymentService.js';
import type { TrackingContext } from '../../service/service-wager/utils/trackWagers.js';
import { WagerService } from '../../service/service-wager/WagerService.js';
import { logger } from '../../utils/index.js';
import type { Wager } from '../../wager/Wager.js';

type WagerPaymentFlowParams<TConfig> = {
  wagers: Wager[];
  config?: TConfig;
  trackingContext?: TrackingContext;
  extraPreMiddleware?: PreMiddlewareFn[];
  extraPostMiddleware?: PostMiddlewareFn[];
  preMiddlewares?: PreMiddlewareFn<Wager>[];
  postMiddlewares?: PostMiddlewareFn<Wager>[];
};

/**
 * Function to pay wagers.
 * Handles middleware setup and flow for paying wagers.
 *
 * @param root0 params
 * @param root0.wagers wagers to pay
 * @param root0.config payment config
 * @param root0.trackingContext optional analytics context for ecommerce payload enrichment
 * @param root0.extraPreMiddleware extra pre middleware to add to flow
 * @param root0.extraPostMiddleware extra post middleware to add to flow
 * @param root0.preMiddlewares pre middleware array to add to flow
 * @param root0.postMiddlewares post middleware array to add to flow
 * @returns result and error
 */
export async function payWagers<TPaymentConfig extends PaymentConfig>({
  wagers,
  config,
  trackingContext,
  extraPreMiddleware,
  extraPostMiddleware,
  preMiddlewares,
  postMiddlewares,
}: WagerPaymentFlowParams<TPaymentConfig>): Promise<
  readonly [PaymentResponseApi<Wager>, CollectionError | undefined, ClientErrorCode | undefined]
> {
  const paymentBuilder = createPayment(new WagerService(config, trackingContext)).addPayable(...wagers);

  if (extraPreMiddleware) {
    for (const middleware of extraPreMiddleware) {
      paymentBuilder.addPreMiddleware(middleware);
    }
  }

  if (preMiddlewares && preMiddlewares.length > 0) {
    for (const middleware of preMiddlewares) {
      paymentBuilder.addPreMiddleware(middleware);
    }
  }

  if (postMiddlewares && postMiddlewares.length > 0) {
    for (const middleware of postMiddlewares) {
      paymentBuilder.addPostMiddleware(middleware);
    }
  }

  if (extraPostMiddleware) {
    for (const middleware of extraPostMiddleware) {
      paymentBuilder.addPostMiddleware(middleware);
    }
  }

  const [result, error, clientErrorCode] = await paymentBuilder.execute();
  const paidWagers = result.getPaidPayableItems();

  for (const wager of paidWagers) {
    if (wager.activatedDrawId) {
      // getAccountBalance.deleteBalance(wager.activatedDrawId);
      logger.debug('Paid wager with activated draw', {
        activatedDrawId: wager.activatedDrawId,
        wagerId: wager.localId,
      });
    }
  }

  return [result, error, clientErrorCode] as const;
}
